import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getModuleEntryByErpModule, getModuleEntryBySlug } from '@/config/moduleEntries';
import { localizeModuleLabel } from '@/i18n/navLabel';
import { ORG_SIGN_IN_PATH } from '@/constants/authRoutes';
import { LoginForm } from '@/pages/auth/LoginForm';
import { AppLauncher } from '@/components/layout/AppLauncher';
import { clearPlatformOnboardingRequired } from '@/modules/onboarding/onboardingState';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setWorkspaceModule, switchWorkspace } from '@/store/authSlice';
import { getLauncherModules, shouldShowAppLauncher } from '@/utils/moduleAccess';
import { markWorkspaceModuleUsed } from '@/utils/moduleScope';
import { resolvePostLoginDestination } from '@/utils/postLoginNavigation';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';
import type { ErpModule } from '@/api/platform.api';

interface ModuleLoginPageProps {
  slug: string;
}

export function ModuleLoginPage({ slug }: ModuleLoginPageProps) {
  const { t } = useTranslation(['auth', 'navigation']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { has } = usePermissions();
  const moduleEntry = getModuleEntryBySlug(slug);
  const moduleLabel = moduleEntry
    ? localizeModuleLabel(moduleEntry.erpModule, t, moduleEntry.label)
    : '';
  const {
    isAuthenticated,
    user,
    entryModule,
    workspaceModule,
    enabledModules,
    accessibleModules,
    loading,
    forcePasswordChange,
  } = useAppSelector((s) => s.auth);

  const activeModule = (entryModule ?? workspaceModule) as ErpModule | null;
  const permissions = user?.permissions ?? [];
  const roleCode = user?.roleCode;

  const launcherApps = getLauncherModules(
    enabledModules,
    permissions,
    accessibleModules,
    roleCode,
    has,
  );

  const canAccessTarget =
    moduleEntry != null &&
    launcherApps.some((entry) => entry.erpModule === moduleEntry.erpModule);

  const canSwitchApps = shouldShowAppLauncher(
    enabledModules,
    permissions,
    accessibleModules,
    roleCode,
    has,
  );

  const signedIntoOtherApp =
    Boolean(moduleEntry) &&
    isAuthenticated &&
    Boolean(user) &&
    !user?.masterAdmin &&
    Boolean(activeModule) &&
    activeModule !== moduleEntry?.erpModule &&
    canAccessTarget;

  const resolveTargetLanding = (
    nextPermissions = permissions,
    nextEnabledModules = enabledModules,
    nextAccessibleModules = accessibleModules,
    nextRoleCode = roleCode,
    nextForcePasswordChange = forcePasswordChange,
  ) =>
    resolvePostLoginDestination({
      permissions: nextPermissions,
      enabledModules: nextEnabledModules,
      accessibleModules: nextAccessibleModules,
      entryModule: moduleEntry!.erpModule,
      roleCode: nextRoleCode,
      forcePasswordChange: nextForcePasswordChange,
    });

  const finishModuleLogin = async (result?: {
    permissions?: string[];
    enabledModules?: string[];
    accessibleModules?: string[];
    entryModule?: string | null;
    roleCode?: string | null;
    forcePasswordChange?: boolean;
  }) => {
    if (!moduleEntry) return;
    loginHandledRef.current = true;
    clearPlatformOnboardingRequired();

    if (result?.forcePasswordChange) {
      navigate('/change-password', { replace: true });
      loginHandledRef.current = false;
      return;
    }

    const nextPermissions = result?.permissions ?? permissions;
    const nextEnabledModules = result?.enabledModules ?? enabledModules;
    const nextAccessibleModules = result?.accessibleModules ?? accessibleModules;
    const nextRoleCode = result?.roleCode ?? roleCode;

    // Pin workspace to this module *before* navigation so layouts never treat
    // /hr (etc.) as a stale POS route and bounce to /pos/dashboard.
    dispatch(setWorkspaceModule(moduleEntry.erpModule));
    markWorkspaceModuleUsed(moduleEntry.erpModule);

    const landing = resolveTargetLanding(
      nextPermissions,
      nextEnabledModules,
      nextAccessibleModules,
      nextRoleCode,
      result?.forcePasswordChange ?? false,
    );
    navigate(landing, { replace: true });
    loginHandledRef.current = false;
  };

  const loginHandledRef = useRef(false);

  useEffect(() => {
    if (!moduleEntry || !isAuthenticated || !user || user.masterAdmin) return;
    if (signedIntoOtherApp) return;
    if (loginHandledRef.current) return;
    if (activeModule === moduleEntry.erpModule) {
      dispatch(setWorkspaceModule(moduleEntry.erpModule));
      navigate(resolveTargetLanding(), { replace: true });
    }
  }, [
    moduleEntry,
    isAuthenticated,
    user,
    activeModule,
    signedIntoOtherApp,
    permissions,
    enabledModules,
    accessibleModules,
    roleCode,
    navigate,
    dispatch,
  ]);

  if (!moduleEntry) {
    return <Navigate to={ORG_SIGN_IN_PATH} replace />;
  }

  const openTargetApp = async () => {
    try {
      dispatch(setWorkspaceModule(moduleEntry.erpModule));
      await dispatch(switchWorkspace(moduleEntry.erpModule)).unwrap();
      dispatch(setWorkspaceModule(moduleEntry.erpModule));
      clearPlatformOnboardingRequired();
      markWorkspaceModuleUsed(moduleEntry.erpModule);
      navigate(resolveTargetLanding(), { replace: true });
      notify.t.success('toast.openedModule', { module: moduleLabel, ns: 'auth' });
    } catch {
      notify.error(t('errors.couldNotOpenModule', { module: moduleLabel }));
    }
  };

  if (signedIntoOtherApp) {
    const currentLabel = localizeModuleLabel(
      activeModule!,
      t,
      getModuleEntryByErpModule(activeModule!)?.label,
    );
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('moduleLogin.signedInAs', { email: user?.email })}
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {t('moduleLogin.currentlyIn', { current: currentLabel })}{' '}
            {t('moduleLogin.openInstead', { target: moduleLabel })}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              disabled={loading}
              onClick={() => void openTargetApp()}
            >
              {t('moduleLogin.openModule', { module: moduleLabel })}
            </button>
            {activeModule && canSwitchApps && launcherApps.length >= 2 ? (
              <AppLauncher activeModule={activeModule} />
            ) : null}
          </div>
        </div>
        <LoginForm
          moduleEntry={moduleEntry}
          showRegisterLink
          onLoginSuccess={(result) => void finishModuleLogin(result)}
        />
      </div>
    );
  }

  if (isAuthenticated && loading) {
    return (
      <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
        {t('moduleLogin.openingModule', { module: moduleLabel })}
      </div>
    );
  }

  return (
    <LoginForm
      moduleEntry={moduleEntry}
      showRegisterLink
      onLoginSuccess={(result) => void finishModuleLogin(result)}
    />
  );
}
